import type { AxiosError } from "axios";

import { axios } from "~/core/axios";
import { checkIfEmailIsWorkEmail } from "~/core/generic/common-utils";
import type {
  BackendApiErrorResponse,
  BackendApiResponse,
} from "~/core/types/backend-api-response.type";
import type { SelectOptionType } from "~/core/types/common.type";

import type { GetOrgCompanyRow } from "./company-data";
import type { GetOrgPeopleRow } from "./people-data";
import type {
  AddNewOrgAttributeBody,
  CopilotSearchedCompanyAndPeople,
} from "./types";

type UpdateOrgObjectAttributeOrderBody = {
  object_type: string;
  attributes: Array<{
    id: string;
    order: number;
  }>;
};

export const updateOrgObjectAttributeOrder = async (
  data: UpdateOrgObjectAttributeOrderBody,
) => {
  return axios.patch<BackendApiResponse>("/org/object/attribute-order", data);
};

export const getCopilotSearchedCompanyAndPeople = async (
  searchText: string,
  {
    abortController,
  }: {
    abortController: AbortController;
  } = {
    abortController: new AbortController(),
  },
) => {
  try {
    const { data } = await axios.get<
      BackendApiResponse<CopilotSearchedCompanyAndPeople[]>
    >(`/org/object/search?q=${searchText}`, {
      signal: abortController.signal,
    });
    return data.data || [];
  } catch (error) {
    const err = error as AxiosError<BackendApiErrorResponse>;
    console.error(err.response);
    return [];
  }
};

export const postAddOrgAttribute = async (data: AddNewOrgAttributeBody) => {
  await axios.post<BackendApiResponse>("/org/object/new-attribute", data);
};

export const getSelectAttributeOptions = async (
  orgAttributeProperty: string,
  options: { isOrgAttribute: "true" | "false"; view_attribute_id?: string },
) => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append("is_org_attribute", options.isOrgAttribute);
    if (options.view_attribute_id) {
      searchParams.append("view_attribute_id", options.view_attribute_id);
    }
    const { data } = await axios.get<BackendApiResponse<SelectOptionType[]>>(
      `/org/object/attribute-options/${orgAttributeProperty}/?${searchParams.toString()}`,
    );

    return data.data || [];
  } catch (error) {
    const err = error as AxiosError<BackendApiErrorResponse>;
    console.error(err.response);
    return [];
  }
};

export const getEnrichmentDataByEmail = async (email: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("email", email);
  if (checkIfEmailIsWorkEmail(email)) {
    searchParams.append("domain", email?.split("@")[1]);
  }
  try {
    const { data } = await axios.get<
      BackendApiResponse<{
        company: GetOrgCompanyRow;
        person: GetOrgPeopleRow;
      }>
    >(`/org/object/enrichments/?${searchParams.toString()}`);

    return data.data;
  } catch (error) {
    const err = error as AxiosError<BackendApiErrorResponse>;
    console.error(err.response);
    return null;
  }
};
